//@ts-nocheck
import React, { ReactElement } from 'react';
import { createEditor, Node, Editor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import useRenderedAt from 'hooks/use-rendered-at';
import Controls from 'components/text-editor/components/controls';
import If from 'components/if-statement';
import { ErrorMessage, Container, textEditorClassName, Leaf } from './style';
import deleteCustomBlockHOF from 'components/text-editor/hooks/use-custom-editor/actions/delete-custom-block';
import getFocusedNodeHOF from 'components/text-editor/hooks/use-custom-editor/actions/get-focused-node';
import updateAnnotationHOF, {
  CustomDataInput,
} from 'components/text-editor/hooks/use-custom-editor/actions/update-annotation';
import getTextFromNode from 'components/text-editor/hooks/use-custom-editor/actions/get-text-from-node';
import { isString, omit } from 'lodash';
import { AnyObject, AnnotationData } from 'types';
import useTextSelectionListeners from 'components/text-editor/components/controls/hooks/use-text-selection-listeners';
import {
  CustomEditor,
  useCustomEditorFromEditor,
} from 'components/text-editor/hooks/use-custom-editor';

interface SlateData {
  data: AnyObject;
  text?: string;
}

export interface TextEditorProps {
  value?: any;
  placeholder?: string;
  onChange: (data: SlateData) => void;
  invalid?: boolean;
  readOnly?: boolean;
  errorMessage?: string;
  className?: string;
  customAnnotationRenderer?: (input: CustomAnnotationInput) => ReactElement;
  markupMenuRenderer?: () => ReactElement;
  showFinishLinkAction?: boolean;
  showDeleteLinkAction?: (data: AnnotationData) => boolean;
  onTextSnippetSave?: (data: AnnotationData) => void;
  onTextSnippetDelete?: (data: AnnotationData) => void;
  onDeleteLink?: (data: AnnotationData) => void;
  onStartLinking?: (data: AnnotationData) => void;
  onFinishLinking?: (data: AnnotationData) => void;
  onGlobalLinkIndicatorClick?: (data: AnnotationData) => void;
  onCopy?: (data: AnyObject[]) => void;
  onCut?: (data: AnyObject[]) => void;
  onPaste?: (data: AnyObject) => void;
  allGlobalLinkIndicatorsVisible?: boolean;
  onLoad?(editor: CustomEditor, editorValue: any): void;
  isNote?: boolean;
}

interface CustomAnnotationInput {
  element: any;
  children: any;
  attributes: any;
  text?: string;
  editorRenderedAt?: string;
  deleteAnnotation: Function;
  updateAnnotation?: (customData: CustomDataInput) => void;
  allGlobalLinkIndicatorsVisible?: boolean;
}

function TextEditor({
  onChange,
  placeholder,
  className,
  errorMessage,
  invalid,
  ...props
}: TextEditorProps) {
  const editor = React.useMemo(() => withReact(createEditor()), []);

  const [editorValue, setEditorValue] = React.useState(getDefaultValue(props));

  const customEditor = useCustomEditorFromEditor(editor);

  const copiedText = React.useRef<string | undefined>();

  const renderedAt = useRenderedAt();

  useTextSelectionListeners(editor);

  React.useEffect(() => {
    props?.onLoad && props?.onLoad(customEditor, editorValue);
  }, []);

  const onTextChange = React.useCallback((data) => {
    // If snippet is last element in editor then we want to add blank space to the end. Otherwise whatever user types at the end will look like it's in the snippet.
    const [lastParagraph, ...rest] = [...data].reverse();

    const [lastLeaf] = [...lastParagraph.children].reverse();

    if ((lastLeaf && lastLeaf.isLink) || lastLeaf.name) {
      const newLastParagraph = {
        ...lastParagraph,
        children: [...lastParagraph.children, { type: 'paragraph', text: ' ' }],
      };

      setEditorValue([...rest.reverse(), newLastParagraph]);
    } else {
      setEditorValue(data);
    }

    onChange({ data, text: serialize(data) });
  }, []);

  const renderLeaf = React.useCallback(
    ({ leaf, attributes, children, ...rest }) => {
      const decorations = omit(leaf, ['type', 'text', 'name']);

      function deleteAnnotation() {
        if (leaf.annotationId) {
          return deleteCustomBlockHOF(editor)(leaf.annotationId);
        }
      }

      function updateAnnotation(customData: CustomDataInput) {
        const { annotationId } = customData;

        if (annotationId) {
          const domNode = document.getElementById(annotationId);

          if (domNode) {
            const domRange = document.createRange();

            domRange.setStart(domNode, 0);
            domRange.setEnd(domNode, 0);

            const at = ReactEditor.toSlateRange(editor, domRange, {
              exactMatch: true,
            });

            at && updateAnnotationHOF(editor)(customData, { at });
          }
        }
      }

      if (props.customAnnotationRenderer && leaf.annotationId) {
        const text = getTextFromNode(leaf as any);

        return props.customAnnotationRenderer({
          text,
          element: leaf,
          attributes: { ...attributes, ...decorations },
          children,
          deleteAnnotation,
          updateAnnotation,
          editorRenderedAt: renderedAt,
          allGlobalLinkIndicatorsVisible: props.allGlobalLinkIndicatorsVisible,
        });
      }

      return (
        <Leaf {...attributes} {...decorations} $isNote={props.isNote}>
          {children}
        </Leaf>
      );
    },
    [props.allGlobalLinkIndicatorsVisible],
  );

  function getCustomNodesInSelection() {
    if (editor.selection) {
      const at = editor.selection;
      const match = (node) => node.type === 'custom';

      return Array.from(Editor.nodes(editor, { at, match, mode: 'all' })).map(
        ([node]) => node,
      );
    }
  }

  return (
    <Container className={className} $invalid={invalid}>
      <Slate editor={editor} value={editorValue} onChange={onTextChange}>
        {!props.readOnly && (
          <Controls
            onTextSnippetSave={props.onTextSnippetSave}
            onTextSnippetDelete={props.onTextSnippetDelete}
            onStartLinking={props.onStartLinking}
            onFinishLinking={props.onFinishLinking}
            onDeleteLink={props.onDeleteLink}
            showDeleteLinkAction={props.showDeleteLinkAction}
            showFinishLinkAction={props.showFinishLinkAction}
            renderedAt={renderedAt}
          />
        )}
        <Editable
          readOnly={props.readOnly}
          tabIndex={0}
          renderLeaf={renderLeaf}
          onPaste={(event) => {
            if (copiedText.current) {
              Editor.insertText(editor, copiedText.current);
              copiedText.current = undefined;

              return event.preventDefault();
            }

            const node =
              editor.selection && getFocusedNodeHOF(editor)(editor.selection);

            if (node) {
              const annotationsData = {
                annotationId: node?.annotationId as string,
                text: getTextFromNode(node),
                name: node?.name,
                isLink: node?.isLink,
              };

              props.onPaste && props.onPaste(annotationsData);
            }
          }}
          onCopy={() => {
            copiedText.current = window.getSelection()?.toString();
          }}
          onCut={() => {
            copiedText.current = undefined;
            //mark them as deleted then add new one on paste when they ar
            const cutNodes = getCustomNodesInSelection() as any[];

            const annotationsData = cutNodes?.map((node) => ({
              annotationId: node?.annotationId as string,
              text: getTextFromNode(node),
              name: node?.name,
              isLink: node?.isLink,
            }));

            props.onCut && props.onCut(annotationsData);
          }}
          className={textEditorClassName}
          placeholder={placeholder}
          required
          spellCheck={'false'}
        />
      </Slate>
      <If condition={invalid}>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </If>
    </Container>
  );
}

TextEditor.defaultProps = {
  value: '',
  onChange: () => undefined,
};

function serialize(value) {
  return value.map((n) => Node.string(n)).join('\n');
  /*return value
    .map(Node.string)
    .map((value) => (value === '' ? '\n' : value))
    .join('');*/
}

function getDefaultValue(props) {
  const value = props.value?.data;

  if (value && isString(value)) {
    return JSON.parse(value);
  }

  return (
    value || [
      { children: [{ type: 'paragraph', text: props.value?.text || '' }] },
    ]
  );
}

export default TextEditor;
